import React, { useEffect, useMemo, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import html2pdf from 'html2pdf.js';
import {
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, functions, storage } from '../../firebase';

const paymentMethods = [
  { value: 'efectivo', label: 'Efectivo' },
  { value: 'tarjeta', label: 'Tarjeta' },
  { value: 'cheque', label: 'Cheque / Money Order' },
  { value: 'transferencia', label: 'Transferencia / Zelle' },
];

const computeRemaining = (invoice = {}) => {
  if (typeof invoice.remaining === 'number') return Number(invoice.remaining);
  const total = Number(invoice.total || 0);
  const deposit = Number(invoice.deposit || 0);
  const discount = Number(invoice.discount || 0);
  const payments = (invoice.payments || []).reduce(
    (sum, entry) => sum + Number(entry.amount || 0),
    0
  );
  return Math.max(total - deposit - discount - payments, 0);
};

const TechnicianDashboard = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [invoiceInfo, setInvoiceInfo] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceError, setInvoiceError] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ amount: '', method: 'efectivo', notes: '' });
  const [savingPayment, setSavingPayment] = useState(false);
  const [savingSignature, setSavingSignature] = useState(false);
  const [updatingId, setUpdatingId] = useState('');
  const [actionFeedback, setActionFeedback] = useState(null);
  const signatureRef = useRef(null);
  const auth = getAuth();

  useEffect(() => {
    const fetchTasks = async () => {
      setLoading(true);
      setError(null);
      try {
        const techQuery = query(
          collection(db, 'operations_tasks'),
          where('type', 'in', ['technician', 'delivery'])
        );
        const snap = await getDocs(techQuery);
        const data = snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
        setTasks(
          data.sort((a, b) => (b.updatedAt?.toMillis?.() || 0) - (a.updatedAt?.toMillis?.() || 0))
        );
      } catch (err) {
        console.error('❌ Error cargando tareas de técnicos:', err);
        setError('No pudimos cargar tus asignaciones.');
      } finally {
        setLoading(false);
      }
    };
    fetchTasks();
  }, []);

  const grouped = useMemo(() => {
    const result = { today: [], upcoming: [], blocked: [] };
    const now = Date.now();
    const oneDay = 24 * 60 * 60 * 1000;
    tasks.forEach((task) => {
      const due = task.dueDate?.toMillis?.() || (task.dueDate?.seconds || 0) * 1000 || null;
      if (task.status === 'blocked') {
        result.blocked.push(task);
      } else if (due && due - now <= oneDay && due - now >= 0) {
        result.today.push(task);
      } else {
        result.upcoming.push(task);
      }
    });
    return result;
  }, [tasks]);

  const timeline = tasks
    .flatMap((task) =>
      (task.timeline || []).map((entry) => ({
        ...entry,
        taskId: task.id,
        orderId: task.relatedOrderId,
        createdAt: entry.createdAt?.toDate?.() || null,
      }))
    )
    .filter((entry) => entry.createdAt)
    .sort((a, b) => b.createdAt - a.createdAt)
    .slice(0, 6);

  const backlog = useMemo(() => tasks.filter((task) => task.status !== 'completed'), [tasks]);

  const updateLocalTask = (taskId, updates) => {
    setTasks((prev) => prev.map((task) => (task.id === taskId ? { ...task, ...updates } : task)));
  };

  const handleStatusChange = async (taskId, status, options = {}) => {
    if (!taskId) return;
    const { timelineNote: providedNote = '', skipPrompt = false } = options;
    let timelineNote = providedNote;
    if (!skipPrompt && status === 'completed') {
      timelineNote =
        window.prompt(
          'Notas finales para el cliente y operaciones (ej. balance, incidencias, firma):',
          timelineNote
        ) || timelineNote;
    }
    setUpdatingId(taskId);
    setActionFeedback(null);
    try {
      const callable = httpsCallable(functions, 'updateOperationalTaskStatus');
      await callable({ taskId, status, timelineNote });
      updateLocalTask(taskId, { status });
      setActionFeedback({ type: 'success', message: 'Estado actualizado correctamente.' });
    } catch (err) {
      console.error('❌ Error actualizando tarea de técnicos:', err);
      setActionFeedback({
        type: 'error',
        message: err.message || 'No pudimos actualizar esta tarea. Intenta nuevamente.',
      });
    } finally {
      setUpdatingId('');
      setTimeout(() => setActionFeedback(null), 4000);
    }
  };

  const resetInvoiceState = () => {
    setInvoiceInfo(null);
    setInvoiceError(null);
    setInvoiceLoading(false);
    setPaymentForm({ amount: '', method: 'efectivo', notes: '' });
    signatureRef.current?.clear?.();
  };

  const closeModal = () => {
    setSelectedTask(null);
    resetInvoiceState();
  };

  const loadInvoice = async (task) => {
    if (!task?.client?.email || !task?.relatedInvoiceId) {
      setInvoiceError('No se especificó la factura asociada.');
      setInvoiceInfo(null);
      return;
    }
    setInvoiceLoading(true);
    setInvoiceError(null);
    try {
      const email = task.client.email.toLowerCase();
      const clientSnap = await getDocs(
        query(collection(db, 'clients'), where('email', '==', email), limit(1))
      );
      if (clientSnap.empty) {
        setInvoiceError('No encontramos el cliente con ese correo.');
        setInvoiceInfo(null);
        return;
      }
      const clientDoc = clientSnap.docs[0];
      const invoiceRef = doc(db, 'clients', clientDoc.id, 'invoices', task.relatedInvoiceId);
      const invoiceSnap = await getDoc(invoiceRef);
      if (!invoiceSnap.exists()) {
        setInvoiceError('La factura vinculada no existe o fue eliminada.');
        setInvoiceInfo(null);
        return;
      }
      setInvoiceInfo({ id: invoiceSnap.id, clientDocId: clientDoc.id, ...invoiceSnap.data() });
    } catch (err) {
      console.error('❌ Error cargando factura:', err);
      setInvoiceError('No pudimos obtener la factura asociada.');
      setInvoiceInfo(null);
    } finally {
      setInvoiceLoading(false);
    }
  };

  const openTaskModal = (task) => {
    setSelectedTask(task);
    resetInvoiceState();
    loadInvoice(task);
  };

  const handlePaymentInput = (e) => {
    const { name, value } = e.target;
    setPaymentForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleRegisterPayment = async (e) => {
    e.preventDefault();
    if (!invoiceInfo?.clientDocId || !invoiceInfo?.id) return;
    const amount = Number(paymentForm.amount);
    if (!amount || amount <= 0) {
      setActionFeedback({ type: 'error', message: 'Ingresa un monto válido.' });
      return;
    }
    const remaining = computeRemaining(invoiceInfo);
    if (amount > remaining) {
      setActionFeedback({
        type: 'error',
        message: 'El monto supera el balance pendiente.',
      });
      return;
    }
    setSavingPayment(true);
    try {
      const invoiceRef = doc(db, 'clients', invoiceInfo.clientDocId, 'invoices', invoiceInfo.id);
      const paymentRecord = {
        amount,
        method: paymentForm.method,
        notes: paymentForm.notes || null,
        recordedBy: auth.currentUser?.email || 'technician',
        recordedAt: serverTimestamp(),
      };
      await updateDoc(invoiceRef, {
        payments: arrayUnion(paymentRecord),
        remaining: Math.max(remaining - amount, 0),
        updatedAt: serverTimestamp(),
      });
      setInvoiceInfo((prev) => ({
        ...prev,
        payments: [...(prev.payments || []), { ...paymentRecord, recordedAt: new Date() }],
        remaining: Math.max(remaining - amount, 0),
      }));
      setPaymentForm({ amount: '', method: 'efectivo', notes: '' });
      setActionFeedback({ type: 'success', message: 'Pago registrado correctamente.' });
    } catch (err) {
      console.error('❌ Error registrando pago:', err);
      setActionFeedback({
        type: 'error',
        message: err.message || 'No pudimos registrar el pago.',
      });
    } finally {
      setSavingPayment(false);
      setTimeout(() => setActionFeedback(null), 4000);
    }
  };

  const buildCompletionHtml = (invoice, task, signatureUrl) => {
    const payments = invoice.payments || [];
    const paymentList = payments
      .map(
        (entry) =>
          `<li>${entry.amount?.toFixed ? entry.amount.toFixed(2) : entry.amount} · ${
            entry.method || 'método'
          }</li>`
      )
      .join('');
    return `
      <div style="font-family: Helvetica, Arial, sans-serif; padding: 16px; color: #111827;">
        <h2 style="margin-top:0;border-bottom:2px solid #047857;padding-bottom:8px;">Confirmación de instalación</h2>
        <p><strong>Orden:</strong> ${task.relatedOrderId || task.title || task.id}</p>
        <p><strong>Cliente:</strong> ${invoice.clientName || invoice.clientInfo?.name || task.client?.name || ''}</p>
        <p><strong>Ubicación:</strong> ${task.client?.projectLocation || 'Pendiente'}</p>
        <p><strong>Balance pendiente:</strong> $${computeRemaining(invoice).toFixed(2)}</p>
        ${
          paymentList
            ? `<div><p><strong>Pagos previos:</strong></p><ul>${paymentList}</ul></div>`
            : ''
        }
        <p><strong>Fecha:</strong> ${new Date().toLocaleString('es-PR')}</p>
        <p><strong>Firma del cliente:</strong></p>
        <img src="${signatureUrl}" alt="firma" style="width:300px;height:150px;border:1px solid #ddd;" />
      </div>
    `;
  };

  const handleSaveSignature = async () => {
    if (!selectedTask || !invoiceInfo?.clientDocId || !invoiceInfo?.id) return;
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      setActionFeedback({
        type: 'error',
        message: 'Captura la firma del cliente antes de guardar.',
      });
      return;
    }
    setSavingSignature(true);
    try {
      const signatureDataUrl = signatureRef.current.getTrimmedCanvas().toDataURL('image/png');
      const html = buildCompletionHtml(invoiceInfo, selectedTask, signatureDataUrl);
      const pdfBlob = await html2pdf()
        .from(html)
        .set({
          margin: 0.5,
          filename: `Instalacion-${selectedTask.relatedOrderId || invoiceInfo.id}.pdf`,
          html2canvas: { scale: 2 },
          jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
        })
        .outputPdf('blob');
      const storagePath = `invoices/${invoiceInfo.clientDocId}/${invoiceInfo.id}-completion.pdf`;
      const pdfRef = ref(storage, storagePath);
      await uploadBytes(pdfRef, pdfBlob, { contentType: 'application/pdf' });
      const completionUrl = await getDownloadURL(pdfRef);
      const invoiceRef = doc(db, 'clients', invoiceInfo.clientDocId, 'invoices', invoiceInfo.id);
      await updateDoc(invoiceRef, {
        completionPdfUrl: completionUrl,
        completionSignature: signatureDataUrl,
        completionAt: serverTimestamp(),
        completionBy: auth.currentUser?.email || 'technician',
      });
      const clientRef = doc(db, 'clients', invoiceInfo.clientDocId);
      const completionRecord = {
        type: 'installation_confirmation',
        url: completionUrl,
        taskId: selectedTask.id,
        invoiceId: invoiceInfo.id,
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.email || 'technician',
        notes: selectedTask.client?.projectLocation || null,
      };
      await updateDoc(clientRef, {
        documents: arrayUnion(completionRecord),
        updatedAt: serverTimestamp(),
      });
      setInvoiceInfo((prev) => ({
        ...prev,
        completionPdfUrl: completionUrl,
        completionSignature: signatureDataUrl,
      }));
      await handleStatusChange(selectedTask.id, 'completed', {
        skipPrompt: true,
        timelineNote: 'Instalación completada con firma digital en sitio.',
      });
      setActionFeedback({ type: 'success', message: 'Firma guardada y tarea completada.' });
    } catch (err) {
      console.error('❌ Error guardando firma:', err);
      setActionFeedback({
        type: 'error',
        message: err.message || 'No pudimos guardar la firma.',
      });
    } finally {
      setSavingSignature(false);
      setTimeout(() => setActionFeedback(null), 4000);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Técnicos & Entregas</h1>
        <p className="text-sm text-slate-600">
          Revisa las instalaciones asignadas, registra pagos y captura la firma del cliente.
        </p>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <section className="grid gap-4 md:grid-cols-3">
        {[
          { label: 'Hoy', value: grouped.today.length, tone: 'text-indigo-600' },
          { label: 'Próximas', value: grouped.upcoming.length, tone: 'text-emerald-600' },
          { label: 'Bloqueadas', value: grouped.blocked.length, tone: 'text-rose-600' },
        ].map((card) => (
          <article
            key={card.label}
            className="rounded-2xl border border-white bg-white p-4 shadow-sm"
          >
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
              {card.label}
            </p>
            <p className={`mt-2 text-3xl font-semibold ${card.tone}`}>{card.value}</p>
          </article>
        ))}
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        {['today', 'upcoming', 'blocked'].map((bucket) => (
          <div key={bucket} className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">
                {bucket === 'today'
                  ? 'Instalaciones hoy'
                  : bucket === 'upcoming'
                  ? 'Programadas'
                  : 'En espera'}
              </h2>
              <button
                type="button"
                className="text-xs font-semibold text-emerald-600 hover:underline"
                onClick={() => {
                  const firstTask = grouped[bucket]?.[0];
                  if (firstTask) openTaskModal(firstTask);
                }}
                disabled={(grouped[bucket] || []).length === 0}
              >
                Ver detalle
              </button>
            </div>
            {loading ? (
              <p className="text-sm text-gray-500">Cargando…</p>
            ) : grouped[bucket].length === 0 ? (
              <p className="text-sm text-gray-500">Sin asignaciones.</p>
            ) : (
              <ul className="mt-3 space-y-3">
                {grouped[bucket].slice(0, 5).map((task) => (
                  <li
                    key={task.id}
                    className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 text-sm"
                  >
                    <p className="font-semibold text-slate-900">
                      {task.relatedOrderId || task.title || task.id}
                    </p>
                    <p className="text-xs text-slate-600">
                      Cliente: {task.client?.name || 'Sin asignar'} ·{' '}
                      {task.client?.projectLocation || 'Ubicación pendiente'}
                    </p>
                    <p className="text-xs text-slate-500">
                      {task.description || task.metadata?.notes || 'Sin notas'}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Backlog activo</h2>
            <p className="text-sm text-slate-600">Controla estados, cobros y entregas en campo.</p>
          </div>
          <span className="rounded-full bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700">
            {backlog.length} pendientes
          </span>
        </div>
        {actionFeedback && (
          <div
            className={`mt-3 rounded-2xl border px-4 py-2 text-sm ${
              actionFeedback.type === 'error'
                ? 'border-rose-200 bg-rose-50 text-rose-700'
                : 'border-emerald-200 bg-emerald-50 text-emerald-700'
            }`}
          >
            {actionFeedback.message}
          </div>
        )}
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-100 text-sm">
            <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-600">
              <tr>
                <th className="px-4 py-2 text-left">Orden</th>
                <th className="px-4 py-2 text-left">Cliente</th>
                <th className="px-4 py-2 text-left">Estado</th>
                <th className="px-4 py-2 text-left">Notas</th>
                <th className="px-4 py-2 text-left">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {backlog.slice(0, 10).map((task) => (
                <tr key={task.id}>
                  <td className="px-4 py-3 font-semibold text-slate-900">
                    {task.relatedOrderId || task.title || task.id}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {task.client?.name || 'Sin asignar'}
                  </td>
                  <td className="px-4 py-3 capitalize text-gray-600">{task.status || 'pending'}</td>
                  <td className="px-4 py-3 text-gray-500">
                    {task.description || task.metadata?.notes || 'Sin notas'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => openTaskModal(task)}
                        className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                      >
                        Abrir
                      </button>
                      {task.status === 'pending' && (
                        <button
                          type="button"
                          onClick={() => handleStatusChange(task.id, 'in_progress')}
                          disabled={updatingId === task.id}
                          className="rounded-full border border-indigo-200 px-3 py-1 text-xs font-semibold text-indigo-700 hover:bg-indigo-50 disabled:opacity-60"
                        >
                          {updatingId === task.id ? 'Actualizando…' : 'En ruta'}
                        </button>
                      )}
                      {task.status !== 'completed' && (
                        <button
                          type="button"
                          onClick={() => handleStatusChange(task.id, 'completed')}
                          disabled={updatingId === task.id}
                          className="rounded-full border border-emerald-200 px-3 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-60"
                        >
                          {updatingId === task.id ? 'Actualizando…' : 'Marcar completado'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Actividad reciente</h2>
            <p className="text-sm text-slate-600">Últimos reportes enviados desde campo.</p>
          </div>
          <span className="rounded-full bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700">
            {timeline.length} registros
          </span>
        </div>
        {timeline.length === 0 ? (
          <p className="mt-4 text-sm text-gray-500">Aún no hay actualizaciones.</p>
        ) : (
          <ul className="mt-4 space-y-3">
            {timeline.map((entry) => (
              <li
                key={`${entry.taskId}-${entry.createdAt}`}
                className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 text-sm"
              >
                <p className="font-semibold text-slate-900">
                  {entry.action || 'Actualización'} • {entry.orderId || entry.taskId}
                </p>
                <p className="text-xs text-slate-600">{entry.notes || 'Sin notas'}</p>
                <p className="text-xs text-slate-500">
                  {entry.createdAt?.toLocaleString('es-PR', {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })}{' '}
                  · {entry.actor || 'sistema'}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>

      {selectedTask && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
          <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex flex-col gap-3 border-b border-slate-100 pb-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.35em] text-emerald-500">
                  Orden {selectedTask.relatedOrderId || selectedTask.id}
                </p>
                <h3 className="text-2xl font-semibold text-slate-900">
                  {selectedTask.client?.name || 'Cliente pendiente'}
                </h3>
                <p className="text-sm text-slate-600">
                  {selectedTask.client?.projectLocation || 'Ubicación pendiente'}
                </p>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="text-sm font-semibold text-emerald-500 hover:text-emerald-800"
              >
                Cerrar
              </button>
            </div>

            {invoiceLoading ? (
              <p className="py-6 text-sm text-gray-500">Cargando factura asociada…</p>
            ) : invoiceError ? (
              <p className="py-6 text-sm text-rose-600">{invoiceError}</p>
            ) : invoiceInfo ? (
              <div className="mt-6 space-y-6">
                <section className="grid gap-4 rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4 text-sm text-emerald-900 md:grid-cols-3">
                  <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-emerald-500">Total</p>
                    <p className="text-2xl font-semibold">
                      ${Number(invoiceInfo.total || 0).toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-emerald-500">Depósito</p>
                    <p className="text-2xl font-semibold">
                      ${Number(invoiceInfo.deposit || 0).toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-emerald-500">
                      Balance pendiente
                    </p>
                    <p className="text-2xl font-semibold">
                      ${computeRemaining(invoiceInfo).toFixed(2)}
                    </p>
                  </div>
                </section>

                <section className="rounded-2xl border border-slate-100 bg-white p-4">
                  <h4 className="text-sm font-semibold text-slate-900">Pagos registrados</h4>
                  {(invoiceInfo.payments || []).length === 0 ? (
                    <p className="text-xs text-slate-500">No hay pagos registrados.</p>
                  ) : (
                    <ul className="mt-2 space-y-2 text-xs text-slate-600">
                      {(invoiceInfo.payments || []).map((entry, idx) => (
                        <li
                          key={`${invoiceInfo.id}-payment-${idx}`}
                          className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2"
                        >
                          <p className="font-semibold text-slate-800">
                            ${Number(entry.amount || 0).toFixed(2)} · {entry.method || 'método'}
                          </p>
                          <p>
                            {entry.recordedAt?.toDate?.()?.toLocaleString('es-PR', {
                              dateStyle: 'short',
                              timeStyle: 'short',
                            }) || ''}
                          </p>
                          {entry.notes && <p className="text-slate-500">{entry.notes}</p>}
                        </li>
                      ))}
                    </ul>
                  )}
                </section>

                <section className="rounded-2xl border border-emerald-100 bg-white p-4">
                  <h4 className="text-sm font-semibold text-emerald-900">Registrar pago en sitio</h4>
                  <form className="mt-3 grid gap-3 md:grid-cols-3" onSubmit={handleRegisterPayment}>
                    <div>
                      <label className="text-xs font-semibold text-emerald-600">
                        Monto a cobrar
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        name="amount"
                        value={paymentForm.amount}
                        onChange={handlePaymentInput}
                        className="mt-1 w-full rounded-2xl border border-emerald-200 px-3 py-2 text-sm text-emerald-900 focus:border-emerald-500 focus:outline-none"
                        placeholder="Ej. 2500"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-emerald-600">
                        Método de pago
                      </label>
                      <select
                        name="method"
                        value={paymentForm.method}
                        onChange={handlePaymentInput}
                        className="mt-1 w-full rounded-2xl border border-emerald-200 px-3 py-2 text-sm text-emerald-900 focus:border-emerald-500 focus:outline-none"
                      >
                        {paymentMethods.map((method) => (
                          <option key={method.value} value={method.value}>
                            {method.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-emerald-600">Notas</label>
                      <input
                        type="text"
                        name="notes"
                        value={paymentForm.notes}
                        onChange={handlePaymentInput}
                        className="mt-1 w-full rounded-2xl border border-emerald-200 px-3 py-2 text-sm text-emerald-900 focus:border-emerald-500 focus:outline-none"
                        placeholder="Ej. Recibo #123"
                      />
                    </div>
                    <div className="md:col-span-3 text-right">
                      <button
                        type="submit"
                        disabled={savingPayment}
                        className="rounded-full bg-emerald-600 px-5 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-400"
                      >
                        {savingPayment ? 'Guardando…' : 'Registrar pago'}
                      </button>
                    </div>
                  </form>
                </section>

                <section className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <h4 className="text-sm font-semibold text-slate-900">
                      Firma del cliente · Confirmación final
                    </h4>
                    {invoiceInfo.completionPdfUrl && (
                      <a
                        href={invoiceInfo.completionPdfUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs font-semibold text-emerald-700 hover:underline"
                      >
                        Ver PDF guardado
                      </a>
                    )}
                  </div>
                  <div className="mt-3 rounded-2xl border border-dashed border-slate-300 bg-white">
                    <SignatureCanvas
                      ref={signatureRef}
                      canvasProps={{ className: 'w-full h-48 rounded-2xl' }}
                    />
                  </div>
                  <div className="mt-3 flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={() => signatureRef.current?.clear?.()}
                      className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100"
                    >
                      Limpiar
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveSignature}
                      disabled={savingSignature}
                      className="rounded-full bg-emerald-600 px-5 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-400"
                    >
                      {savingSignature ? 'Guardando…' : 'Guardar firma y finalizar'}
                    </button>
                  </div>
                </section>
              </div>
            ) : (
              <p className="py-6 text-sm text-gray-500">No se encontró la factura asociada.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TechnicianDashboard;
